import { ReactComponent as ImageIcon } from 'assets/icons/image24.svg'
import { useEditorView } from 'common/Prose/EditorProvider/hooks/useEditorView'
import { insertAndFocus } from 'common/Prose/utils/insertAndFocus'
import { Spinner } from 'common/Spinner/Spinner'
import { Button } from 'components/Button'
import { REACT_APP_CLOUDINARY_CLOUD_NAME } from 'env'
import { Node as ProseNode } from 'prosemirror-model'
import { useRef, useState } from 'react'
import { SelectedImage } from 'types'
import { reportError } from 'utils/error'
import MStyles from '../../MobileToolbar.module.scss'
import ImageUploadButtonStyles from './ImageUploadButton.module.scss'

interface Props {
  uploadHighRes?: boolean
  multiSelect?: boolean
}

// TODO: Stop refreshing this on every MobileToolbar change
export const ImageUploadButton = ({ uploadHighRes = false, multiSelect = true }: Props) => {
  const [isUploading, setIsUploading] = useState(false)
  const editorView = useEditorView()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Derived from src/proseShared/prompts/MediaUploadPrompt/MediaUploadPrompt.tsx
  const uploadImages = async (selectedImages: SelectedImage[] = []) => {
    setIsUploading(true)

    const formData = new FormData()
    const payload = []

    try {
      for (let selectedImage of selectedImages) {
        // cloudinary accepts either a blob or url
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const fileUrl = uploadHighRes ? selectedImage.full_resolution_url! : selectedImage.url
        const fileToUpload = selectedImage.file ? selectedImage.file : fileUrl
        formData.append('file', fileToUpload)
        formData.append('upload_preset', 'workspace_portal_uploads')

        const response = await fetch(
          `https://api.cloudinary.com/v1_1/${REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
          {
            method: 'POST',
            body: formData,
          },
        )
        const parsedResponse = await response.json()
        payload.push(parsedResponse)
      }
    } catch (error) {
      reportError(error, { severity: 'error' })
    }

    setIsUploading(false)

    return payload
  }

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const files = Array.from(event.target.files!)
    const selectedImages = files.map((file) => {
      const image: SelectedImage = {
        url: URL.createObjectURL(file),
        file: file,
      }
      return image
    })

    uploadAndInsertImages(selectedImages)
  }

  const uploadAndInsertImages = async (selectedImages: SelectedImage[] = []) => {
    const uploadedImages = await uploadImages(selectedImages)

    if (uploadedImages) {
      uploadedImages.forEach(({ url }: { url: string }) => {
        const node = editorView.state.schema.nodes.image.create({ src: url }) as ProseNode
        insertAndFocus(editorView, node)
      })
    }
  }

  const handleButtonClick = () => {
    if (fileInputRef.current && !isUploading) {
      fileInputRef.current.click()
    }
  }

  let buttonIcon = isUploading ? <Spinner /> : <ImageIcon />

  if (isUploading) {
    buttonIcon = <Spinner />
  }

  return (
    <>
      <Button
        preventFocusOnPress
        variant="none"
        size="sm"
        className={MStyles.button}
        onClick={handleButtonClick}
        isDisabled={isUploading}
        aria-label="Image Upload Button"
      >
        {buttonIcon}
      </Button>
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        onChange={handleFileInputChange}
        multiple={multiSelect}
        className={ImageUploadButtonStyles.insertImageInput}
        aria-hidden="true"
        aria-label="Image File Input"
      />
    </>
  )
}
